import React, { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useUserContext } from 'providers/UserProvider';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import meApi from 'api/modules/meApi';

const TenantDropdown = () => {
  const { user, selectedTenant, isSuperuser, isLoading } = useUserContext();
  const [isChangingTenant, setIsChangingTenant] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { warehouse } = useParams();

  if (!isSuperuser || isLoading) return null;

  const tenantList = user?.tenants || [];

  const onChange = async tenant => {
    try {
      setIsChangingTenant(true);
      const schemaName = tenant?.domains?.[0]?.domain;

      // me API를 호출하여 사용자가 해당 tenant+warehouse에 접근 가능한지 확인
      const me = await meApi.getMe();

      // 선택한 tenant가 사용자의 tenant 목록에 있는지 확인
      const validTenant = me.tenants.find(t => t.id === tenant.id);

      if (!validTenant) {
        toast.error('해당 tenant에 접근 권한이 없습니다.');
        setIsChangingTenant(false);
        return;
      }

      // 사용자가 접근 가능한 모든 warehouse 가져오기
      // For superusers, use the tenant-specific warehouse permissions
      const warehouses =
        me.is_superuser && validTenant.warehouse_permissions
          ? validTenant.warehouse_permissions.map(p => p.warehouse)
          : me.warehouse_permissions?.map(p => p.warehouse) || [];

      if (!warehouses || warehouses.length === 0) {
        toast.error('접근 가능한 창고가 없습니다.');
        setIsChangingTenant(false);
        return;
      }

      // 권한 있는 warehouse 중 첫 번째 사용
      // TenantWarehouseGuard에서 URL 기반으로 접근 권한을 다시 확인함
      const allowedWarehouse = warehouses[0];

      if (schemaName && allowedWarehouse) {
        // 현재 suffix 경로 추출
        const parts = location.pathname.split('/').filter(Boolean);
        // [tenant, warehouse, ...suffix]
        const suffix = parts.slice(2).join('/');

        const newPath = `/${schemaName}/${allowedWarehouse.domain}${
          suffix ? '/' + suffix : ''
        }`;

        navigate(newPath, { replace: true });
      } else {
        toast.error('Tenant 변경 실패: 접근 가능한 창고가 없습니다.');
        console.error('Tenant Change Failed: No Warehouse', {
          schemaName,
          tenant,
          allowedWarehouse,
        });
      }
    } catch (error) {
      console.error('Tenant Change Error:', error);
      toast.error('Tenant 변경 중 오류가 발생했습니다.');
    } finally {
      setIsChangingTenant(false);
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="falcon-default"
        className="text-truncate"
        style={{ maxWidth: 220, marginRight: 10 }}
        disabled={isChangingTenant}
      >
        {isChangingTenant ? (
          <span>
            <span
              className="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            Loading...
          </span>
        ) : (
          selectedTenant?.name || 'Select Tenant'
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-menu-end"
        style={{ width: 260, maxHeight: 400, overflowY: 'auto' }}
      >
        {tenantList.map(t => (
          <Dropdown.Item
            key={t.id}
            onClick={() => onChange(t)}
            active={selectedTenant?.id === t.id}
            className="text-truncate"
            title={t.name}
            disabled={isChangingTenant}
          >
            {t.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TenantDropdown;
